import React, { useState, useEffect, useRef } from 'react'
import { match } from 'react-router'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { getAbortable } from '../utils/api'
import queryString from 'query-string'
import { History } from 'history'

export type WithSimpleApiProps<ApiData> = {
  history: History
  match: match
  url: string
  apiData: ApiData
  refresh: () => void
  searchStr: string
  searchParams: object
}

const withSimpleApi = <ApiData, P extends WithSimpleApiProps<ApiData>>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentType => {
  const Component = (props: P) => {
    const match = useRouteMatch()
    const history = useHistory()
    const { search } = useLocation()
    const urlForApi = match.url.substr(1)

    const [apiData, setApiData] = useState<ApiData>()
    const [error, setError] = useState(false)
    const [firstDownloaded, setFirstDownloaded] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const downloadAbort = useRef(() => {})
    const downloadFinished = useRef(false)

    const download = () => {
      setDownloading(true)

      const [postPromise, abort] = getAbortable<ApiData>(urlForApi + search)

      downloadAbort.current = abort
      downloadFinished.current = false

      postPromise
        .then(data => {
          downloadFinished.current = true
          setApiData(data)
        })
        .catch(err => {
          downloadFinished.current = true
          setError(true)
          console.error(
            urlForApi,
            err.error ? err.error : err,
            err.error && err.error.exception[0]
          )
        })
        .finally(() => {
          setDownloading(false)

          if (!firstDownloaded) {
            setFirstDownloaded(true)
          }
        })
    }

    useEffect(download, [search, urlForApi])

    // useEffect(() => {
    //   if (!downloading) {
    //     const body = document.querySelector('.layout-body')

    //     if (body) {
    //       body.scrollTo(0, 0)
    //     }
    //   }
    // }, [downloading])

    useEffect(() => {
      return () => {
        downloadAbort.current()

        if (!downloadFinished.current) {
          console.warn('cleanup unfinished')
        }
      }
    }, [])

    if (downloading) {
      return (
        <div className="loading">
          <span className="fa fa-spin fa-spinner-third" />
        </div>
      )
    }

    if (error) {
      return <>Error</>
    }

    return firstDownloaded ? (
      <WrappedComponent
        {...props}
        history={history}
        match={match}
        url={urlForApi}
        apiData={apiData}
        refresh={download}
        searchStr={search}
        searchParams={queryString.parse(search)}
      />
    ) : null
  }

  return Component as React.ComponentType
}

export default withSimpleApi
